import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// smart_template_controller
//
// Provides a "Generate with AI" button on the task template form.
// On click, POSTs to suggest_template and renders Accept/Dismiss chips
// for each suggested checklist item.
//
// Usage:
//   <div data-controller="smart-template"
//        data-smart-template-url-value="<url>"
//        data-smart-template-target-input-id-value="task_template_checklist_items">
//     <button type="button" data-action="smart-template#suggest"
//             data-smart-template-target="button">Generate with AI</button>
//     <div data-smart-template-target="chips"></div>
//     <span data-smart-template-target="status"></span>
//   </div>

export default class extends Controller {
  static targets = ["button", "chips", "status"]
  static values = { url: String, targetInputId: String }

  connect() {
    this.loading = false
  }

  async suggest() {
    if (this.loading) return
    this.#setLoading(true)
    this.#clearChips()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#csrfToken(),
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (!response.ok) {
        this.#showStatus(data.error || t("javascript.controllers.smart_template_controller.could_not_generate"), "error")
        return
      }

      const items = data.checklist_items || []
      if (items.length === 0) {
        this.#showStatus(t("javascript.controllers.smart_template_controller.no_suggestions"), "info")
        return
      }

      this.#renderChips(items)
      this.#showStatus("", "")
    } catch (e) {
      this.#showStatus(t("javascript.controllers.smart_template_controller.network_error"), "error")
    } finally {
      this.#setLoading(false)
    }
  }

  acceptAll() {
    const chips = this.chipsTarget.querySelectorAll("[data-item-text]")
    const texts = Array.from(chips).map(c => c.dataset.itemText)
    this.#addItemsToList(texts)
    this.chipsTarget.innerHTML = ""
    this.#showStatus(t("javascript.controllers.smart_template_controller.added_items", { count: texts.length }), "success")
  }

  accept(event) {
    const chip = event.currentTarget.closest("[data-item-text]")
    if (!chip) return
    const text = chip.dataset.itemText
    this.#addItemsToList([text])
    chip.remove()
    if (this.chipsTarget.childElementCount === 0) {
      this.#showStatus(t("javascript.controllers.smart_template_controller.item_added"), "success")
    }
  }

  dismiss(event) {
    const chip = event.currentTarget.closest("[data-item-text]")
    chip?.remove()
    if (this.chipsTarget.childElementCount === 0) {
      this.#showStatus("", "")
    }
  }

  // -- private helpers -------------------------------------------------------

  #addItemsToList(texts) {
    const inputId = this.targetInputIdValue || "task_template_checklist_items"
    const input = document.getElementById(inputId)
    if (!input) return

    let existing = []
    try { existing = JSON.parse(input.value || "[]") } catch {}
    const newItems = texts.map((text, idx) => ({
      question_text: text,
      question_type: "checkbox",
      required: false,
      position: existing.length + idx + 1
    }))
    input.value = JSON.stringify(existing.concat(newItems))
    input.dispatchEvent(new CustomEvent("json-checklist:refresh"))
  }

  #setLoading(state) {
    this.loading = state
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = state
      const icon = this.buttonTarget.querySelector(".material-symbols-outlined")
      const label = this.buttonTarget.querySelector(".btn-label")
      if (label) label.textContent = state ? t("javascript.controllers.smart_template_controller.generating") : t("javascript.controllers.smart_template_controller.generate_with_ai")
    }
  }

  #clearChips() {
    if (this.hasChipsTarget) this.chipsTarget.innerHTML = ""
  }

  #showStatus(message, type) {
    if (!this.hasStatusTarget) return
    const colours = { error: "text-red-500", success: "text-green-600", info: "text-gray-500" }
    this.statusTarget.className = `text-sm mt-1 ${colours[type] || "text-gray-500"}`
    this.statusTarget.textContent = message
  }

  #renderChips(items) {
    const fragment = document.createDocumentFragment()

    // "Accept All" header
    const header = document.createElement("div")
    header.className = "flex items-center justify-between mb-2"
    header.innerHTML = `
      <span class="text-sm font-medium text-gray-700">${t("javascript.controllers.smart_template_controller.ai_suggestions_count", { count: items.length })}</span>
      <button type="button" data-action="smart-template#acceptAll"
              class="px-2 py-1 rounded bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium transition-colors">
        ${t("javascript.controllers.smart_template_controller.accept_all")}
      </button>
    `
    fragment.appendChild(header)

    items.forEach(text => {
      const chip = document.createElement("div")
      chip.dataset.itemText = text
      chip.className = "flex items-center gap-2 mt-1 p-2 bg-violet-50 border border-violet-200 rounded-lg text-sm"
      chip.innerHTML = `
        <span class="material-symbols-outlined text-violet-500 !text-base">checklist</span>
        <span class="flex-1 text-gray-800">${this.#escape(text)}</span>
        <div class="flex gap-1 shrink-0">
          <button type="button" data-action="smart-template#accept"
                  class="px-2 py-0.5 rounded bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium transition-colors">
            ${t("javascript.controllers.smart_template_controller.accept")}
          </button>
          <button type="button" data-action="smart-template#dismiss"
                  class="px-2 py-0.5 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium transition-colors">
            ${t("javascript.controllers.smart_template_controller.dismiss")}
          </button>
        </div>
      `
      fragment.appendChild(chip)
    })

    this.chipsTarget.appendChild(fragment)
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  #escape(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
